import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../utils/api';

/**
 * PRIVACY FIX 2026-11-03: Reset all per-user in-memory + persisted stores on
 * every successful login/registration. Without this, switching users without a
 * full page reload (e.g. session expiry -> login page -> new user) leaks the
 * previous user's emails, compose tabs, and compose window into the new user's
 * session. This guarantees a fresh, isolated mailbox for every user.
 */
function resetStoresForNewUser() {
  // 1) Wipe in-memory mail state so stale emails/searchQuery never bleed across users.
  import('../stores/mailStore').then((m) => {
    m.resetMailTracking();
    m.useMailStore.setState({
      emails: [],
      total: 0,
      currentFolder: 'inbox',
      searchQuery: '',
      searchFilters: {},
      selectedEmail: null,
      selectedEmails: [],
      page: 1,
    });
  }).catch(() => {});

  // 2) Clear persisted compose tabs + compose window so a new user never sees
  //    another user's draft or an auto-opened compose window.
  import('../stores/composeTabsStore').then((c) => {
    c.useComposeTabsStore.getState().clearAllTabs();
    c.useComposeTabsStore.setState({
      chatOpen: false,
      viewingChat: false,
      docsOpen: false,
      viewingDocs: false,
      sheetsOpen: false,
      viewingSheets: false,
      aiOpen: false,
      viewingAi: false,
      panelOrder: [],
    });
  }).catch(() => {});
  try {
    localStorage.removeItem('zmail-compose-tabs');
  } catch { /* ignore */ }
}

function extractErrorMessage(error: any, fallback: string): string {
  const data = error?.response?.data;
  if (typeof data?.message === 'string' && data.message.trim()) return data.message;
  if (Array.isArray(data?.message)) return data.message.filter(Boolean).join(', ');
  if (typeof data === 'string' && data.trim()) return data;
  if (error?.response?.status) return `${fallback} (HTTP ${error.response.status}). Please try again.`;
  if (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error') return 'Cannot reach the server. Check your internet connection and try again.';
  return error?.message || fallback;
}

export interface User {
  id: string;
  email: string;
  username: string;
  firstName?: string;
  lastName?: string;
  avatar?: string;
  role: string;
  domain: string;
  isActive: boolean;
  createdAt: string;
}

interface TwoFactorRequired {
  requires2FA: boolean;
  email: string;
  phoneHint: string;
  emailHint?: string;
  otpMethod?: 'sms' | 'email' | 'totp';
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  twoFactorRequired: TwoFactorRequired | null;
  
  // Actions
  login: (email: string, password: string) => Promise<boolean>;
  verifyOtp: (email: string, otp: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  clearError: () => void;
  clearTwoFactor: () => void;
  setUser: (user: User) => void;
  setAuth: (data: { user: Partial<User>; token: string; refreshToken?: string }) => void;
}

interface RegisterData {
  email: string;
  password: string;
  username: string;
  firstName?: string;
  lastName?: string;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      twoFactorRequired: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null, twoFactorRequired: null });
        try {
          const response = await api.post('/auth/user/login', { email, password });
          const data = response.data?.data || response.data;
          
          // Check if 2FA is required
          if (data.requires2FA) {
            set({
              twoFactorRequired: {
                requires2FA: true,
                email: data.email,
                phoneHint: data.phoneHint || '',
                emailHint: data.emailHint || '',
                otpMethod: data.method || data.otpMethod || 'email',
              },
              isLoading: false,
            });
            return true; // Indicates 2FA is required
          }
          
          if (data.accessToken) {
            const rawUser = data.user || {};
            // PRIVACY FIX: reset all per-user stores before switching to the new user
            resetStoresForNewUser();
            set({
              user: {
                ...rawUser,
                domain: rawUser.domain?.name || rawUser.email?.split('@')[1] || '',
                isActive: rawUser.isActive ?? true,
                username: rawUser.username || rawUser.email?.split('@')[0] || '',
              },
              token: data.accessToken,
              isAuthenticated: true,
              isLoading: false,
            });
            localStorage.setItem('accessToken', data.accessToken);
            localStorage.setItem('token', data.accessToken);
            // H-06: refresh token is stored in an httpOnly cookie by the backend —
            // never persist it to localStorage (XSS cannot read the cookie).
            return false; // Indicates no 2FA, login complete
          } else {
            throw new Error('Invalid response from server');
          }
        } catch (error: any) {
          const message = extractErrorMessage(error, 'Login failed');
          set({ error: message, isLoading: false });
          throw new Error(message);
        }
      },

      verifyOtp: async (email: string, otp: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post('/auth/user/verify-otp', { email, otp, loginDomain: window.location.hostname });
          const data = response.data?.data || response.data;
          
          if (data.accessToken) {
            const rawUser2 = data.user || {};
            // PRIVACY FIX: reset all per-user stores before switching to the new user
            resetStoresForNewUser();
            set({
              user: {
                ...rawUser2,
                domain: rawUser2.domain?.name || rawUser2.email?.split('@')[1] || '',
                isActive: rawUser2.isActive ?? true,
                username: rawUser2.username || rawUser2.email?.split('@')[0] || '',
              },
              token: data.accessToken,
              isAuthenticated: true,
              isLoading: false,
              twoFactorRequired: null,
            });
            localStorage.setItem('accessToken', data.accessToken);
            localStorage.setItem('token', data.accessToken);
            // H-06: refresh token is in the httpOnly cookie — not stored in localStorage.
          } else {
            throw new Error('Invalid response from server');
          }
        } catch (error: any) {
          const message = extractErrorMessage(error, 'OTP verification failed');
          set({ error: message, isLoading: false });
          throw new Error(message);
        }
      },

      clearTwoFactor: () => set({ twoFactorRequired: null, error: null }),

      register: async (data: RegisterData) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post('/auth/register', data);
          const result = response.data?.data || response.data;
          
          if (result.accessToken) {
            // PRIVACY FIX: reset all per-user stores before switching to the new user
            resetStoresForNewUser();
            set({
              user: result.user,
              token: result.accessToken,
              isAuthenticated: true,
              isLoading: false,
            });
            localStorage.setItem('accessToken', result.accessToken);
            localStorage.setItem('token', result.accessToken);
            // H-06: refresh token is in the httpOnly cookie — not stored in localStorage.
          }
        } catch (error: any) {
          const message = extractErrorMessage(error, 'Registration failed');
          set({ error: message, isLoading: false });
          throw new Error(message);
        }
      },

      logout: () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('token');
        // H-06: refresh token lives in the httpOnly cookie; the backend clears it on logout.
        // Remove any legacy refreshToken that may linger from before this fix.
        localStorage.removeItem('refreshToken');
        // Clear all persisted stores to prevent data leakage between users
        localStorage.removeItem('zmail-compose-tabs');
        localStorage.removeItem('zmail-settings');
        localStorage.removeItem('zmail-theme');
        // Reset mail tracking so re-login doesn't fire notifications for old emails
        import('../stores/mailStore').then(m => {
          m.resetMailTracking();
          // Also wipe in-memory mail state so stale emails/searchQuery
          // never bleed from one session to the next without a page reload.
          m.useMailStore.setState({
            emails: [],
            total: 0,
            currentFolder: 'inbox',
            searchQuery: '',
            searchFilters: {},
            selectedEmail: null,
            selectedEmails: [],
            page: 1,
          });
        }).catch(() => {});
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          error: null,
          twoFactorRequired: null,
        });
      },

      refreshUser: async () => {
        const token = get().token || localStorage.getItem('accessToken');
        if (!token) return;

        try {
          const response = await api.get('/users/profile');
          const rawUser = response.data?.data || response.data;
          // Normalize: domain may be a Domain object or string, isActive must be boolean
          const user = {
            ...rawUser,
            domain: rawUser.domain?.name || rawUser.email?.split('@')[1] || get().user?.domain || '',
            isActive: rawUser.isActive ?? get().user?.isActive ?? true,
            username: rawUser.username || rawUser.email?.split('@')[0] || '',
            avatar: rawUser.avatarUrl || rawUser.avatar || get().user?.avatar || undefined,
          };
          set({ user, isAuthenticated: true });
        } catch {
          get().logout();
        }
      },

      clearError: () => set({ error: null }),
      
      setUser: (user: User) => set({ user }),

      setAuth: (data: { user: Partial<User>; token: string; refreshToken?: string }) => {
        const user = {
          id: data.user.id || '',
          email: data.user.email || '',
          username: data.user.email?.split('@')[0] || '',
          firstName: data.user.firstName,
          lastName: data.user.lastName,
          role: data.user.role || 'user',
          domain: data.user.email?.split('@')[1] || '',
          isActive: true,
          createdAt: new Date().toISOString(),
        } as User;

        localStorage.setItem('accessToken', data.token);
        // H-06: refresh token is in the httpOnly cookie — not stored in localStorage.

        set({
          user,
          token: data.token,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
